/**
 * Pricing validation utilities for the cart frontend.
 *
 * These utilities ensure we never display incorrect prices to customers
 * by failing explicitly rather than silently falling back to base prices.
 *
 * IMPORTANT: consumerPriceCents is the canonical price that includes all fees.
 * If it's missing, we must NOT fall back to priceCents (which excludes fees).
 */

/**
 * Validates that a rental option has proper consumer pricing.
 * @param {Object} option - Rental option from API
 * @returns {boolean} True if pricing is valid
 */
export function hasValidPricing(option) {
    return (
        option &&
        typeof option.consumerPriceCents === 'number' &&
        option.consumerPriceCents > 0
    )
}

/**
 * Gets the consumer price from an option, throwing if missing.
 * @param {Object} option - Rental option from API
 * @param {string} context - Description for error messages
 * @returns {number} Price in cents
 * @throws {Error} If consumerPriceCents is missing
 */
export function getConsumerPriceCents(option, context = 'unknown') {
    if (!hasValidPricing(option)) {
        const error = new Error(
            `Missing consumerPriceCents for ${context}. ` +
            `Got: ${JSON.stringify(option?.consumerPriceCents)}`
        )
        console.error('[Pricing Error]', error.message, { option, context })
        throw error
    }
    return option.consumerPriceCents
}

/**
 * Safely gets the minimum consumer price from a product's rental options.
 * Returns null if any option is missing valid pricing (fail-safe).
 *
 * @param {Object} product - Product with rentalOptions array
 * @returns {{ minPriceCents: number, hasValidPricing: boolean, reason?: string, invalidOptions?: string[] }}
 */
export function getMinConsumerPrice(product) {
    const options = product?.rentalOptions || []

    if (options.length === 0) {
        // No options available - check if product itself has pricing
        if (product?.consumerPriceCents && product.consumerPriceCents > 0) {
            return { minPriceCents: product.consumerPriceCents, hasValidPricing: true }
        }
        return { minPriceCents: 0, hasValidPricing: false, reason: 'no_options' }
    }

    const validPrices = []
    const invalidOptions = []

    for (const opt of options) {
        if (hasValidPricing(opt)) {
            validPrices.push(opt.consumerPriceCents)
        } else {
            invalidOptions.push(opt.id || opt.label || 'unknown')
        }
    }

    if (invalidOptions.length > 0) {
        console.error(
            '[Pricing Error] Missing consumerPriceCents for options:',
            invalidOptions,
            'in product:',
            product?.displayName || product?.key
        )
    }

    // If ANY option is missing pricing, we can't trust the min
    if (invalidOptions.length > 0) {
        return {
            minPriceCents: 0,
            hasValidPricing: false,
            reason: 'missing_consumer_price',
            invalidOptions,
        }
    }

    return {
        minPriceCents: Math.min(...validPrices),
        hasValidPricing: true,
    }
}

/**
 * Formats cents to display string with validation.
 * @param {number} cents - Price in cents
 * @returns {string} Formatted price or error indicator
 */
export function formatPrice(cents) {
    if (typeof cents !== 'number' || isNaN(cents) || cents < 0) {
        return '—' // Em dash indicates unavailable
    }
    return `$${(cents / 100).toFixed(2)}`
}
